import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { TrackedLink } from './tracked-link';
import { retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from '@app/_components/_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class TrackedLinkService {

	constructor(
		private http: HttpClient,
		private alertService: AlertService,
		private sharedService: SharedService
	) { }

	urlEndpoint = 'tracked-links';

	trackingModes = [
		{ value: 'REDIRECT', name: 'Tiny URL (Redirect)' },
		{ value: 'APPEND', name: 'Token Append' }
	];

	sourceTypes = [
		{ value: 'EMAIL', name: 'Email' },
		{ value: 'SMS', name: 'SMS' },
		{ value: 'SOCIAL', name: 'Social Media' },
		{ value: 'WEBSITE', name: 'Website' },
		{ value: 'QR', name: 'QR Code' },
		{ value: 'DIRECT', name: 'Direct' },
		{ value: 'BLOG', name: 'Blog' }
	];

	statuses = [
		{ value: 'ACTIVE', name: 'Active' },
		{ value: 'DRAFT', name: 'Draft' },
		{ value: 'PAUSED', name: 'Paused' },
		{ value: 'EXPIRED', name: 'Expired' },
		{ value: 'ARCHIVED', name: 'Archived' }
	];

	add(trackedLink: TrackedLink): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, trackedLink);
	}

	update(trackedLink: TrackedLink): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, trackedLink);
	}

	delete(trackedLinkId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${trackedLinkId}`);
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		if (dateFrom) { params = params.set('createDateFrom', dateFrom); }
		if (dateTo) { params = params.set('createDateTo', dateTo); }
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	// ─── Analytics ───────────────────────────────────────────────────────

	getLinkAnalytics(trackedLinkId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${trackedLinkId}/analytics`).pipe(retry({ count: 2, delay: 1500 }));
	}

	getLinkClicks(trackedLinkId: number, page: number, size: number): Observable<ApiResponse> {
		let params = new HttpParams()
			.set('page', page.toString())
			.set('size', size.toString())
			.set('sort', 'clickedAt,desc');
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${trackedLinkId}/clicks`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getSummary(dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = new HttpParams();
		if (dateFrom) { params = params.set('dateFrom', dateFrom); }
		if (dateTo) { params = params.set('dateTo', dateTo); }
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/summary`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getBySource(dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = new HttpParams();
		if (dateFrom) { params = params.set('dateFrom', dateFrom); }
		if (dateTo) { params = params.set('dateTo', dateTo); }
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-source`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getTopLinks(limit: number = 10): Observable<ApiResponse> {
		let params = new HttpParams().set('limit', limit.toString());
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/top-links`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getTemplateAnalytics(templateId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = new HttpParams().set('templateId', templateId.toString());
		if (dateFrom) { params = params.set('dateFrom', dateFrom); }
		if (dateTo) { params = params.set('dateTo', dateTo); }
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-template`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	// ─── Campaign Analytics ─────────────────────────────────────────────

	getCampaigns(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/campaigns`).pipe(retry({ count: 2, delay: 1500 }));
	}

	getCampaignAnalytics(campaignId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = new HttpParams().set('campaignId', campaignId.toString());
		if (dateFrom) { params = params.set('dateFrom', dateFrom); }
		if (dateTo) { params = params.set('dateTo', dateTo); }
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-campaign`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	// ─── Recipient Detail ───────────────────────────────────────────────

	private buildDateParams(id: number, idName: string, dateFrom?: string, dateTo?: string): HttpParams {
		let params = new HttpParams().set(idName, id.toString());
		if (dateFrom) { params = params.set('dateFrom', dateFrom); }
		if (dateTo) { params = params.set('dateTo', dateTo); }
		return params;
	}

	getTemplateAllRecipients(templateId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-template/all`,
			{ params: this.buildDateParams(templateId, 'templateId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getTemplateOpenedRecipients(templateId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-template/opened`,
			{ params: this.buildDateParams(templateId, 'templateId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getTemplateClickedRecipients(templateId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-template/clicked`,
			{ params: this.buildDateParams(templateId, 'templateId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getTemplateUrlClicks(templateId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-template/url-clicks`,
			{ params: this.buildDateParams(templateId, 'templateId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getCampaignAllRecipients(campaignId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-campaign/all`,
			{ params: this.buildDateParams(campaignId, 'campaignId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getCampaignOpenedRecipients(campaignId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-campaign/opened`,
			{ params: this.buildDateParams(campaignId, 'campaignId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getCampaignClickedRecipients(campaignId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-campaign/clicked`,
			{ params: this.buildDateParams(campaignId, 'campaignId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getCampaignUrlClicks(campaignId: number, dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/link-analytics/by-campaign/url-clicks`,
			{ params: this.buildDateParams(campaignId, 'campaignId', dateFrom, dateTo) }).pipe(retry({ count: 2, delay: 1500 }));
	}
}
